/* ═══════════════════════════════════════════════════════════════════════
   Theme, and the tenant's colour.

   The interface takes the practice's accent so their clients feel they are
   dealing with them, not with us. That means accepting a colour chosen by
   someone who was picking a logo, not designing an interface — so it gets
   checked before it's used anywhere text has to sit on top of it.
   ═══════════════════════════════════════════════════════════════════════ */

export type Theme = "light" | "dark";

/** crAIg's own colour. The default, and the fallback for anything unusable. */
export const CRAIG_ACCENT = "#E2603A";

export const prefersDark = (): boolean =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches);

export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

export const hasFinePointer = (): boolean =>
  typeof window !== "undefined" && Boolean(window.matchMedia?.("(pointer: fine)").matches);

/* ── Colour maths ───────────────────────────────────────────────────────── */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

const toHex = ({ r, g, b }: Rgb) =>
  "#" + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");

/** WCAG relative luminance. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

const contrast = (a: Rgb, b: Rgb): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
};

const mix = (a: Rgb, b: Rgb, t: number): Rgb => ({
  r: a.r + (b.r - a.r) * t,
  g: a.g + (b.g - a.g) * t,
  b: a.b + (b.b - a.b) * t,
});

const BLACK: Rgb = { r: 0, g: 0, b: 0 };
const WHITE: Rgb = { r: 255, g: 255, b: 255 };

/** The card colour each theme paints buttons on. Mirrors styles.css. */
const SURFACE: Record<Theme, string> = { light: "#ffffff", dark: "#1c1c1f" };

/**
 * Applies a tenant's accent, adjusted until it actually works.
 *
 * A brand colour is chosen for a logo, not an interface, so two separate
 * things have to be checked — and getting only the first right is a trap I
 * fell into once already:
 *
 *   1. Can you FIND the button? A pale yellow filled shape on a white card
 *      measured 1.28:1 — perfectly readable text on a button nobody can
 *      see. WCAG 1.4.11 wants 3:1 for non-text UI, so the colour is nudged
 *      toward the theme's extreme until the button has an edge.
 *
 *   2. Can you READ the label? Only once the fill is settled is the label
 *      colour chosen, by measured contrast rather than assumption — a
 *      mid-yellow needs dark text, a navy needs white.
 *
 * Adjusting rather than rejecting matters: mixing toward black or white
 * holds the hue, so the practice still recognises their colour, and it
 * still works. Anything unparseable falls back to crAIg's own.
 */
export function applyAccent(hex: string | undefined, theme: Theme): void {
  const root = document.documentElement;
  const surface = parseHex(SURFACE[theme])!;
  let accent = (hex ? parseHex(hex) : null) ?? parseHex(CRAIG_ACCENT)!;

  // 1. Make the button findable against the card behind it.
  const toward = theme === "dark" ? WHITE : BLACK;
  let guard = 0;
  while (contrast(accent, surface) < 3 && guard++ < 30) {
    accent = mix(accent, toward, 0.06);
  }

  // 2. Then pick the label colour that survives on it.
  const ink = contrast(accent, WHITE) >= contrast(accent, BLACK) ? "#ffffff" : "#16160f";

  root.style.setProperty("--accent", toHex(accent));
  root.style.setProperty("--accent-ink", ink);
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;

  /* Switch the screen on.
     Applied here rather than remembered on every page, because a surface
     that has the wash and one that doesn't looks like a bug rather than a
     choice. Idempotent, so calling it on every theme change is free. */
  document.body.classList.add("scanlines");
}

/* ── Named schemes ──────────────────────────────────────────────────────
   Mirrors core's SCHEMES. Duplicated deliberately and kept short: the web
   app must be able to resolve a scheme before it has talked to the server,
   and one shared list would mean shipping core to the browser to render a
   colour. The server validates against core's copy, so an id that isn't
   here was never storable in the first place. */

const SCHEME_ACCENTS: Record<string, string> = {
  clay: "#c4603a",
  ocean: "#2e6f5e",
  harbour: "#2b5f86",
  ink: "#3b3f7a",
  plum: "#7a3b63",
  forest: "#3f6b34",
  rust: "#a5482c",
  slate: "#4a5560",
  mustard: "#9a7515",
  berry: "#9c2f45",
  teal: "#1f6f72",
  charcoal: "#3a3a36",
};

/** The colour a business actually chose, however they chose it. */
export function accentFor(
  branding: { accent?: string; scheme?: string } | null | undefined,
): string | undefined {
  if (!branding) return undefined;
  // A typed hex wins: somebody who went to the trouble means it.
  if (branding.accent) return branding.accent;
  return branding.scheme ? SCHEME_ACCENTS[branding.scheme] : undefined;
}

/* ── How the conversation looks ─────────────────────────────────────────
   Returned as inline CSS variables scoped to the chat card, rather than
   set on :root, so a business's choices colour their conversation and
   nothing else on the page — and so the reviewer's case view, which is
   ours, never inherits somebody's typewriter font.

   Mirrors core's lists. See schemes.ts for why both are constrained. */

const SURFACE_TINTS: Record<string, string> = {
  plain: "",
  warm: "#c98a4b",
  cool: "#4b7fc9",
  sage: "#5f8a63",
  sand: "#b8a06a",
  rose: "#c07a86",
};

const FONT_STACKS: Record<string, string> = {
  system: "",
  rounded: '"SF Pro Rounded", "Segoe UI", "Trebuchet MS"',
  serif: '"Iowan Old Style", Georgia, "Times New Roman"',
  mono: 'ui-monospace, "SF Mono", "Cascadia Mono", Menlo',
};

export function chatStyle(
  chat: { surface?: string; font?: string; bubbles?: string } | undefined,
): React.CSSProperties {
  if (!chat) return {};
  const style: Record<string, string> = {};

  /* Mixed at 7% into whatever the theme's surface already is, so it lands
     the same distance from the ink in light and in dark. A flat colour
     here would have to be chosen twice and would still be wrong on the
     third theme. */
  const tint = chat.surface ? SURFACE_TINTS[chat.surface] : "";
  if (tint) {
    style["--chat-surface"] = `color-mix(in srgb, ${tint} 7%, var(--surface))`;
    style["--chat-surface-2"] = `color-mix(in srgb, ${tint} 12%, var(--surface))`;
  }

  /* PREPENDED. The tail of --font-sans is Noto CJK, Arabic and
     Devanagari, and dropping it would turn eighty languages into boxes
     for the sake of a typeface. */
  const face = chat.font ? FONT_STACKS[chat.font] : "";
  if (face) style["--chat-font"] = `${face}, var(--font-sans)`;

  if (chat.bubbles === "quiet") {
    style["--chat-bubble"] = "var(--surface-2)";
    style["--chat-bubble-ink"] = "var(--ink)";
  }

  return style as React.CSSProperties;
}
